import React, { useEffect, useState } from 'react';
import axios from 'axios';
import HeaderBar from '../components/HeaderBar';
import './ProfilePage.css';

function ProfilePage({ user, onLogout, onUserUpdate }) {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
    const [activeTab, setActiveTab] = useState('profile');
    const [uploadingPicture, setUploadingPicture] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [defaultAvatars, setDefaultAvatars] = useState([]);
    const [showAvatarGallery, setShowAvatarGallery] = useState(false);
    const [selectedDefaultAvatar, setSelectedDefaultAvatar] = useState('');

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await axios.get('/api/profile');
                setProfile(response.data);
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to load profile');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    // Fetch default avatars when picture tab is opened
    useEffect(() => {
        if (activeTab === 'picture') {
            axios.get('/api/profile/default-avatars')
                .then(response => setDefaultAvatars(response.data.avatars))
                .catch(err => console.error('Failed to load avatars:', err));
        }
    }, [activeTab]);

    const handleFieldChange = (field, value) => {
        setProfile((prev) => ({ ...prev, [field]: value }));
    };

    const handleProfileSave = async (event) => {
        event.preventDefault();
        if (!profile) return;

        setError('');
        setSuccess('');
        setSavingProfile(true);

        if (profile.notification_preference === 'whatsapp' && (!profile.phone_country_code || !profile.phone_number)) {
            setSavingProfile(false);
            setError('Please provide your WhatsApp country code and phone number');
            return;
        }

        try {
            const response = await axios.put('/api/profile', {
                name: profile.name,
                alternate_email: profile.alternate_email,
                monitor_preference: profile.monitor_preference,
                notification_preference: profile.notification_preference,
                phone_country_code: profile.phone_country_code,
                phone_number: profile.phone_number,
            });

            setProfile(response.data.user);
            onUserUpdate?.(response.data.user);
            setSuccess('Profile updated successfully');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update profile');
        } finally {
            setSavingProfile(false);
        }
    };

    const handlePasswordSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');

        if (!passwordForm.current || !passwordForm.next || !passwordForm.confirm) {
            setError('Please fill in all password fields');
            return;
        }

        if (passwordForm.next !== passwordForm.confirm) {
            setError('New password and confirmation do not match');
            return;
        }

        if (passwordForm.next.length < 6) {
            setError('New password must be at least 6 characters');
            return;
        }

        setSavingPassword(true);

        try {
            await axios.post('/api/profile/change-password', {
                current_password: passwordForm.current,
                new_password: passwordForm.next,
            });
            setPasswordForm({ current: '', next: '', confirm: '' });
            setSuccess('Password updated successfully');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update password');
        } finally {
            setSavingPassword(false);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSelectedFile(null);
            return;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            event.target.value = '';
            return;
        }

        // Validate file size (500KB = 500 * 1024 bytes)
        const maxSizeBytes = 500 * 1024;
        if (file.size > maxSizeBytes) {
            setError(`Image size must be less than 500KB. Current size: ${(file.size / 1024).toFixed(1)}KB`);
            event.target.value = '';
            return;
        }

        setSelectedFile(file);
        setError('');
    };

    const handlePictureUpload = async (event) => {
        event.preventDefault();
        if (!selectedFile) {
            setError('Please select an image first');
            return;
        }

        setError('');
        setSuccess('');
        setUploadingPicture(true);

        try {
            const formData = new FormData();
            formData.append('profilePicture', selectedFile);

            const response = await axios.post('/api/profile/upload-picture', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setProfile(response.data.user);
            onUserUpdate?.(response.data.user);
            setSuccess('Profile picture uploaded successfully');
            setSelectedFile(null);
            // Reset file input
            document.getElementById('profile-picture').value = '';
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to upload profile picture');
        } finally {
            setUploadingPicture(false);
        }
    };

    const handleToggleAvatarDisplay = async () => {
        setError('');
        setSuccess('');

        try {
            const response = await axios.post('/api/profile/toggle-avatar-display', {
                showAvatar: !profile.show_avatar
            });

            setProfile(response.data.user);
            onUserUpdate?.(response.data.user);

            if (response.data.autoAssigned) {
                setSuccess('Avatar display enabled and a default avatar has been assigned. You can change it below.');
            } else {
                setSuccess(response.data.message);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update avatar display preference');
        }
    };

    const handleSelectDefaultAvatar = async () => {
        if (!selectedDefaultAvatar) {
            setError('Please select an avatar first');
            return;
        }

        setError('');
        setSuccess('');

        try {
            const response = await axios.post('/api/profile/select-default-avatar', {
                avatarName: selectedDefaultAvatar
            });

            setProfile(response.data.user);
            onUserUpdate?.(response.data.user);
            setSuccess('Avatar updated successfully');
            setShowAvatarGallery(false);
            setSelectedDefaultAvatar('');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update avatar');
        }
    };

    if (loading) {
        return (
            <div className="profile-page loading-state">
                <p>Loading profile...</p>
            </div>
        );
    }

    return (
        <div className="profile-page">
            <HeaderBar user={user} onLogout={onLogout} />

            <div className="profile-container">
                {(error || success) && (
                    <div className={`notice ${error ? 'error' : 'success'}`}>
                        {error || success}
                    </div>
                )}

                <section className="profile-card">
                    <div className="profile-tabs">
                        <button
                            className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
                            onClick={() => setActiveTab('profile')}
                        >
                            Profile Details
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'monitor' ? 'active' : ''}`}
                            onClick={() => setActiveTab('monitor')}
                        >
                            User Preferences
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'password' ? 'active' : ''}`}
                            onClick={() => setActiveTab('password')}
                        >
                            Change Password
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'picture' ? 'active' : ''}`}
                            onClick={() => setActiveTab('picture')}
                        >
                            Upload Picture
                        </button>
                    </div>

                    <div className="tab-content">
                        {activeTab === 'profile' && (
                            <div className="tab-panel">
                                <div className="section-header">
                                    <h2>Profile Details</h2>
                                    <span className="section-hint">Update your name and email information.</span>
                                </div>
                                <form onSubmit={handleProfileSave} className="profile-form">
                                    <div className="form-row">
                                        <label htmlFor="profile-name">Full Name</label>
                                        <input
                                            id="profile-name"
                                            type="text"
                                            value={profile?.name || ''}
                                            onChange={(event) => handleFieldChange('name', event.target.value)}
                                            placeholder="Enter your full name"
                                        />
                                    </div>

                                    <div className="form-row">
                                        <label htmlFor="profile-email">Primary Email</label>
                                        <input
                                            id="profile-email"
                                            type="email"
                                            value={profile?.email || ''}
                                            disabled
                                        />
                                    </div>

                                    <div className="form-row">
                                        <label htmlFor="profile-alt-email">Alternate Email</label>
                                        <input
                                            id="profile-alt-email"
                                            type="email"
                                            value={profile?.alternate_email || ''}
                                            onChange={(event) => handleFieldChange('alternate_email', event.target.value)}
                                            placeholder="Optional alternate email"
                                        />
                                    </div>

                                    <div className="form-actions">
                                        <button type="submit" className="btn-primary" disabled={savingProfile}>
                                            {savingProfile ? 'Saving...' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {activeTab === 'monitor' && (
                            <div className="tab-panel">
                                <div className="section-header">
                                    <h2>Preference</h2>
                                    <span className="section-hint">Set your preferred configuration.</span>
                                </div>
                                <form onSubmit={handleProfileSave} className="profile-form">
                                    <div className="form-row">
                                        <label htmlFor="profile-monitor">Monitor Preference</label>
                                        <select
                                            id="profile-monitor"
                                            value={profile?.monitor_preference || 'Single Monitor'}
                                            onChange={(event) => handleFieldChange('monitor_preference', event.target.value)}
                                        >
                                            <option value="Single Monitor">Single Monitor</option>
                                            <option value="Dual Monitor">Dual Monitor</option>
                                            <option value="Adjustable Monitor">Adjustable Monitor</option>
                                            <option value="Any Available">Any Available</option>
                                        </select>
                                    </div>

                                    <div className="form-row">
                                        <label htmlFor="profile-notification">Notification Preference</label>
                                        <select
                                            id="profile-notification"
                                            value={profile?.notification_preference || 'email'}
                                            onChange={(event) => handleFieldChange('notification_preference', event.target.value)}
                                        >
                                            <option value="email">Email</option>
                                            <option value="whatsapp">WhatsApp</option>
                                        </select>
                                    </div>

                                    {profile?.notification_preference === 'whatsapp' && (
                                        <>
                                            <div className="form-row">
                                                <label htmlFor="profile-country-code">Country Code</label>
                                                <input
                                                    id="profile-country-code"
                                                    type="text"
                                                    value={profile?.phone_country_code || ''}
                                                    onChange={(event) => handleFieldChange('phone_country_code', event.target.value)}
                                                    placeholder="e.g. +62"
                                                />
                                            </div>
                                            <div className="form-row">
                                                <label htmlFor="profile-phone">Telephone Number</label>
                                                <input
                                                    id="profile-phone"
                                                    type="tel"
                                                    value={profile?.phone_number || ''}
                                                    onChange={(event) => handleFieldChange('phone_number', event.target.value)}
                                                    placeholder="e.g. 81234567890"
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* Locker Number & PIN Display */}
                                    <div className="form-row" style={{
                                        backgroundColor: '#e8f5e9',
                                        padding: '15px',
                                        borderRadius: '8px',
                                        border: '2px solid #4CAF50',
                                        marginTop: '20px'
                                    }}>
                                        <label style={{ fontWeight: 'bold', fontSize: '14px', color: '#2E7D32' }}>
                                            🔢 Your Locker Number
                                        </label>
                                        <div style={{
                                            fontSize: '28px',
                                            fontWeight: 'bold',
                                            color: '#2E7D32',
                                            textAlign: 'center',
                                            padding: '10px',
                                            backgroundColor: 'white',
                                            borderRadius: '8px',
                                            marginTop: '8px',
                                            fontFamily: 'monospace'
                                        }}>
                                            #{profile?.locker_number || 'Not assigned'}
                                        </div>
                                    </div>


                                    <div className="form-row" style={{
                                        backgroundColor: '#f0f8ff',
                                        padding: '20px',
                                        borderRadius: '8px',
                                        border: '2px solid #0072CE',
                                        marginTop: '15px'
                                    }}>
                                        <label style={{ fontWeight: 'bold', fontSize: '16px', color: '#0072CE' }}>
                                            🔐 Your Locker PIN
                                        </label>
                                        <div style={{
                                            fontSize: '32px',
                                            fontWeight: 'bold',
                                            letterSpacing: '8px',
                                            color: '#0072CE',
                                            textAlign: 'center',
                                            padding: '15px',
                                            backgroundColor: 'white',
                                            borderRadius: '8px',
                                            marginTop: '10px',
                                            fontFamily: 'monospace'
                                        }}>
                                            {profile?.locker_pin || '----'}
                                        </div>
                                        <small style={{ color: '#666', marginTop: '10px', display: 'block', textAlign: 'center' }}>
                                            Use this 4-digit PIN to access your assigned locker
                                        </small>
                                    </div>

                                    <div className="form-actions">
                                        <button type="submit" className="btn-primary" disabled={savingProfile}>
                                            {savingProfile ? 'Saving...' : 'Save Preference'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {activeTab === 'password' && (
                            <div className="tab-panel">
                                <div className="section-header">
                                    <h2>Change Password</h2>
                                    <span className="section-hint">Update your password regularly for security.</span>
                                </div>
                                <form onSubmit={handlePasswordSubmit} className="profile-form">
                                    <div className="form-row">
                                        <label htmlFor="current-password">Current Password</label>
                                        <input
                                            id="current-password"
                                            type="password"
                                            value={passwordForm.current}
                                            onChange={(event) => setPasswordForm((prev) => ({ ...prev, current: event.target.value }))}
                                            placeholder="Enter current password"
                                            required
                                        />
                                    </div>

                                    <div className="form-row">
                                        <label htmlFor="new-password">New Password</label>
                                        <input
                                            id="new-password"
                                            type="password"
                                            value={passwordForm.next}
                                            onChange={(event) => setPasswordForm((prev) => ({ ...prev, next: event.target.value }))}
                                            placeholder="Enter new password"
                                            required
                                            minLength={6}
                                        />
                                    </div>

                                    <div className="form-row">
                                        <label htmlFor="confirm-password">Confirm Password</label>
                                        <input
                                            id="confirm-password"
                                            type="password"
                                            value={passwordForm.confirm}
                                            onChange={(event) => setPasswordForm((prev) => ({ ...prev, confirm: event.target.value }))}
                                            placeholder="Confirm new password"
                                            required
                                            minLength={6}
                                        />
                                    </div>

                                    <div className="form-actions">
                                        <button type="submit" className="btn-secondary" disabled={savingPassword}>
                                            {savingPassword ? 'Updating...' : 'Update Password'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {activeTab === 'picture' && (
                            <div className="tab-panel">
                                <div className="section-header">
                                    <h2>Profile Picture</h2>
                                    <span className="section-hint">Manage your profile avatar display.</span>
                                </div>

                                {/* Avatar Display Toggle */}
                                <div className="avatar-toggle-section">
                                    <label className="toggle-label">
                                        <span>Show Avatar on Profile</span>
                                        <input
                                            type="checkbox"
                                            checked={profile?.show_avatar || false}
                                            onChange={handleToggleAvatarDisplay}
                                            className="toggle-checkbox"
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                    <p className="toggle-hint">
                                        {profile?.show_avatar
                                            ? 'Your profile displays an avatar image'
                                            : 'Your profile displays your name initials only'}
                                    </p>
                                </div>

                                {profile?.show_avatar ? (
                                    <>
                                        {/* Current Avatar Display */}
                                        <div className="upload-section">
                                            <div className="current-avatar">
                                                <div className="avatar-circle large">
                                                    {profile?.profile_picture ? (
                                                        <img
                                                            src={profile.profile_picture}
                                                            alt="Profile"
                                                            className="avatar-image"
                                                            onError={(e) => {
                                                                // Fallback to initials if image fails to load
                                                                e.target.style.display = 'none';
                                                                e.target.parentElement.innerHTML = profile?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
                                                            }}
                                                        />
                                                    ) : (
                                                        profile?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'
                                                    )}
                                                </div>
                                                <p className="avatar-hint">Current Avatar</p>
                                            </div>
                                        </div>

                                        {/* Upload Custom Picture */}
                                        <form onSubmit={handlePictureUpload} className="profile-form">
                                            <div className="form-row">
                                                <label htmlFor="profile-picture">Option 1: Upload Custom Picture</label>
                                                <input
                                                    id="profile-picture"
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleFileSelect}
                                                />
                                                <span className="field-hint">
                                                    {selectedFile
                                                        ? `Selected: ${selectedFile.name} (${(selectedFile.size / 1024).toFixed(1)}KB)`
                                                        : 'Maximum file size: 500KB. Supported formats: JPG, PNG, GIF'}
                                                </span>
                                            </div>
                                            <div className="form-actions">
                                                <button type="submit" className="btn-primary" disabled={uploadingPicture || !selectedFile}>
                                                    {uploadingPicture ? 'Uploading...' : 'Upload Picture'}
                                                </button>
                                            </div>
                                        </form>

                                        <div className="divider">OR</div>

                                        {/* Default Avatar Selection */}
                                        <div className="default-avatar-section">
                                            <label>Option 2: Choose Default Avatar</label>
                                            <button
                                                type="button"
                                                onClick={() => setShowAvatarGallery(!showAvatarGallery)}
                                                className="btn-secondary"
                                            >
                                                {showAvatarGallery ? 'Hide Avatar Gallery' : 'Show Avatar Gallery'}
                                            </button>

                                            {showAvatarGallery && (
                                                <div className="avatar-gallery-container">
                                                    <div className="avatar-gallery">
                                                        {defaultAvatars.map((avatar) => (
                                                            <div
                                                                key={avatar}
                                                                className={`avatar-option ${selectedDefaultAvatar === avatar ? 'selected' : ''}`}
                                                                onClick={() => setSelectedDefaultAvatar(avatar)}
                                                            >
                                                                <img
                                                                    src={`/uploads/device-avatars/default avatar/${avatar}`}
                                                                    alt={avatar}
                                                                    className="avatar-preview"
                                                                />
                                                                {selectedDefaultAvatar === avatar && <div className="avatar-checkmark">✓</div>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={handleSelectDefaultAvatar}
                                                        className="btn-primary"
                                                        disabled={!selectedDefaultAvatar}
                                                    >
                                                        Use Selected Avatar
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="avatar-disabled-message">
                                        <p>Your profile will display your name initials instead of an avatar.</p>
                                        <p className="hint-text">Enable "Show Avatar on Profile" above to upload or select an avatar.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}

export default ProfilePage;
